import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { FormsService } from '../../services/forms.service';
import { Form, FormStatus, FormType } from '../../models/form.model';
import { ToastService } from '../../../../../core/services/toast.service';

// UI Components
import { TextInputComponent } from '../../../../../shared/components/ui/input/components/text-input/text-input.component';
import { TextAreaComponent } from '../../../../../shared/components/ui/input/components/text-area/text-area.component';
import { SelectComponent } from '../../../../../shared/components/ui/input/components/select/select.component';

// Directives
import { UbButtonDirective } from '@/app/shared/components/ui/button.directive';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TextInputComponent,
    TextAreaComponent,
    SelectComponent,
    UbButtonDirective,
  ],
  selector: 'app-public-form',
  template: `
    <div class="min-h-screen bg-gray-50 py-8">
      <div class="max-w-2xl mx-auto px-4">
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <!-- Form Header -->
          <div class="text-center mb-6" *ngIf="form">
            <h1 class="text-2xl font-semibold text-gray-900 mb-2">{{ form.name }}</h1>
            <p class="text-gray-600" *ngIf="form.description">{{ form.description }}</p>
          </div>

          <!-- Loading State -->
          <div *ngIf="loading" class="text-center py-8">
            <svg class="w-8 h-8 mx-auto mb-4 animate-spin text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
            </svg>
            <p class="text-gray-600">Loading form...</p>
          </div>

          <!-- Form Content -->
          <div *ngIf="form && !loading">
            <form [formGroup]="formGroup" (ngSubmit)="onSubmit()" class="space-y-6">
              <!-- Form Fields -->
              <div *ngFor="let field of form.fields; trackBy: trackByField" class="space-y-2">
                <label [for]="field.name" class="block text-sm font-medium text-gray-700">
                  {{ field.label }}
                  <span *ngIf="field.required" class="text-red-500">*</span>
                </label>

                <!-- Text Input -->
                <text-input
                  *ngIf="field.type === 'text' || field.type === 'email' || field.type === 'phone' || field.type === 'url'"
                  [id]="field.name"
                  [formControlName]="field.name"
                  [placeholder]="field.placeholder || ''"
                  [type]="field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'">
                </text-input>

                <!-- Textarea -->
                <text-area
                  *ngIf="field.type === 'textarea'"
                  [id]="field.name"
                  [formControlName]="field.name"
                  [placeholder]="field.placeholder || ''">
                </text-area>

                <!-- Number Input -->
                <text-input
                  *ngIf="field.type === 'number'"
                  [id]="field.name"
                  [formControlName]="field.name"
                  [placeholder]="field.placeholder || ''"
                  type="number">
                </text-input>

                <!-- Date Input -->
                <text-input
                  *ngIf="field.type === 'date'"
                  [id]="field.name"
                  [formControlName]="field.name"
                  [placeholder]="field.placeholder || ''"
                  type="date">
                </text-input>

                <!-- Select -->
                <select-input
                  *ngIf="field.type === 'select'"
                  [id]="field.name"
                  [formControlName]="field.name"
                  [options]="getFieldOptions(field)">
                </select-input>

                <!-- Checkbox -->
                <div *ngIf="field.type === 'checkbox'" class="flex items-center">
                  <input
                    [id]="field.name"
                    [formControlName]="field.name"
                    type="checkbox"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded">
                  <label [for]="field.name" class="ml-2 block text-sm text-gray-900">
                    {{ field.label }}
                  </label>
                </div>

                <!-- Radio -->
                <div *ngIf="field.type === 'radio'" class="space-y-2">
                  <div *ngFor="let option of field.options; trackBy: trackByOption" class="flex items-center">
                    <input
                      [id]="option.value"
                      [formControlName]="field.name"
                      [value]="option.value"
                      type="radio"
                      class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300">
                    <label [for]="option.value" class="ml-2 block text-sm text-gray-900">
                      {{ option.label }}
                    </label>
                  </div>
                </div>

                <!-- Validation Errors -->
                <div *ngIf="formGroup.get(field.name)?.invalid && formGroup.get(field.name)?.touched" class="text-red-600 text-sm">
                  <span *ngIf="formGroup.get(field.name)?.errors?.['required']">This field is required.</span>
                  <span *ngIf="formGroup.get(field.name)?.errors?.['email']">Please enter a valid email address.</span>
                  <span *ngIf="formGroup.get(field.name)?.errors?.['pattern']">Please enter a valid value.</span>
                </div>
              </div>

              <!-- Submit Button -->
              <div class="pt-4">
                <button
                  ubButton
                  type="submit"
                  [disabled]="formGroup.invalid || submitting"
                  class="w-full flex items-center justify-center">
                  <svg *ngIf="submitting" class="w-4 h-4 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                  </svg>
                  {{ submitting ? 'Submitting...' : 'Submit' }}
                </button>
              </div>
            </form>
          </div>

          <!-- Error State -->
          <div *ngIf="error" class="text-center py-8">
            <div class="text-red-600 mb-4">
              <svg class="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
              </svg>
              <p class="text-lg font-medium">{{ error }}</p>
            </div>
          </div>

          <!-- Success State -->
          <div *ngIf="submitted && !submitting" class="text-center py-8">
            <div class="text-green-600 mb-4">
              <svg class="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <p class="text-lg font-medium">Thank you!</p>
              <p class="text-gray-600">{{ form?.successMessage || 'Your submission has been received.' }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  `
})
export class PublicFormComponent implements OnInit {
  form: Form | null = null;
  formGroup: FormGroup;
  loading = true;
  submitting = false;
  submitted = false;
  error: string | null = null;
  workspaceId: string = '';
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private formsService: FormsService,
    private toast: ToastService,
    private fb: FormBuilder
  ) {
    this.formGroup = this.fb.group({});
  }

  ngOnInit() {
    const formId = this.route.snapshot.paramMap.get('id');
    this.workspaceId = this.route.snapshot.paramMap.get('wid') || '';
    if (formId) {
      this.loadForm(formId);
    }
  }

  loadForm(formId: string) {
    this.loading = true;
    this.error = null;

    this.formsService.getPublicFormById(this.workspaceId, formId).subscribe({
      next: (form) => {
        this.form = form;
        this.buildFormGroup();
        this.loading = false;
      },
      error: (error) => {
        this.error = 'Form not found or is not available.';
        this.loading = false;
        console.error('Error loading form:', error);
      }
    });
  }

  buildFormGroup() {
    if (!this.form) return;

    const group: any = {};
    
    this.form.fields.forEach(field => {
      const validators = [];
      
      if (field.required) {
        validators.push(Validators.required);
      }

      if (field.type === 'email') {
        validators.push(Validators.email);
      }

      if (field.validation) {
        if (field.validation.minLength) {
          validators.push(Validators.minLength(field.validation.minLength));
        }
        if (field.validation.maxLength) {
          validators.push(Validators.maxLength(field.validation.maxLength));
        }
        if (field.validation.pattern) {
          validators.push(Validators.pattern(field.validation.pattern));
        }
      }

      group[field.name] = [field.defaultValue || '', validators];
    });

    this.formGroup = this.fb.group(group);
  }

  getFieldOptions(field: any) {
    return field.options?.map((option: any) => ({
      value: option.value,
      label: option.label
    })) || [];
  }

  trackByField(index: number, field: any): string {
    return field.name;
  }

  trackByOption(index: number, option: any): string {
    return option.value;
  }

  onSubmit() {
    if (this.formGroup.invalid || !this.form) return;

    this.submitting = true;
    const formData = this.formGroup.value;

    this.formsService.submitForm({
      formId: this.form.id,
      data: formData,
      workspaceId: this.workspaceId,
    }).subscribe({
      next: (response) => {
        this.submitted = true;
        this.submitting = false;
        this.toast.success('Form submitted successfully!');
      },
      error: (error) => {
        this.submitting = false;
        this.error = 'Failed to submit form. Please try again.';
        console.error('Error submitting form:', error);
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
} 